// src/App.js
import React, { useState } from "react";
import { BrowserRouter as Router, Route, Routes, Link, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import "./App.css";
import ScrollToTop from './ScrollToTop';

// Image imports
import punchImage from './images/punch.webp';
import techImage from './images/tech.webp';
import beltsImage from './images/belts.webp';
import hanImage from './images/han.webp';
import bodyparts from './images/bodyparts.webp';
import stepImage from './images/step.webp';
import stancesImage from './images/stances.webp';
import quizImage from './images/quiz.webp';
import lemonImage from './images/lemon.webp';
import aboutImage from './images/about.webp';
import logoImage from './images/logo.webp';
import googlePlayImage from './images/googleplay.webp';
import appStoreBadge from './images/appstore.webp';

import whitebelt from './images/whitebelt.webp';
import yellowTip from './images/yellowtip.webp';
import yellowBelt from './images/yellowbelt.webp';
import greenTip from './images/greentip.webp';
import greenBelt from './images/greenbelt.webp';
import blueTip from './images/bluetip.webp';
import blueBelt from './images/bluebelt.webp';
import redTip from './images/redtip.webp';
import redBelt from './images/redbelt.webp';
import blackTip from './images/blacktip.webp';
import danOne from './images/danone.webp';
import danTwo from './images/dantwo.webp';
import danThree from './images/danthree.webp';
import danFour from './images/danfour.webp';
import danFive from './images/danfive.webp';
import danSix from './images/dansix.webp';

import threeStepIcon from './images/threestep.webp';
import twoStepIcon from './images/twostep.webp';
import freeSparringIcon from './images/freesparring.webp';


// Import components
import BasicTheory from './BasicTheory';
import Technique from './Technique';
import Korean from './Korean';
import BodyParts from './BodyParts';
import KoreanMatch from './KoreanMatch';
import Stances from './Stances';
import Quiz from './Quiz';
import Sources from './Sources';
import AppInfo from './AppInfo';
import HandbookEditor from './handbookeditor'
import Login from "./Login";
import ProtectedRoute from "./ProtectedRoute";

import Belt10K from './10k';
import Belt9K from './9k';
import Belt8K from './8k';
import Belt7K from './7k';
import Belt6K from './6k';
import Belt5K from './5k';
import Belt4K from './4k';
import Belt3K from './3k';
import Belt2K from './2k';
import Belt1K from './1k';
import Belt1D from './1d';
import Belt2D from './2d';
import Belt3D from './3d';
import Belt4D from './4d';
import Belt5D from './5d';
import Belt6D from './6d';

import ThreeStep from './ThreeStep';
import TwoStep from './TwoStep';
import FreeSparring from './FreeSparring';


// Main Page Component
function MainPage() {
  const [showBelts, setShowBelts] = useState(false);
  const [showSparring, setShowSparring] = useState(false);

  const toggleBelts = () => setShowBelts(prev => !prev);
  const toggleSparring = () => setShowSparring(prev => !prev);

  const belts = [
    { src: whitebelt, alt: "White Belt", label: "10th Kup", path: "/10k" },
    { src: yellowTip, alt: "Yellow Tip", label: "9th Kup", path: "/9k" },
    { src: yellowBelt, alt: "Yellow Belt", label: "8th Kup", path: "/8k" },
    { src: greenTip, alt: "Green Tip", label: "7th Kup", path: "/7k" },
    { src: greenBelt, alt: "Green Belt", label: "6th Kup", path: "/6k" },
    { src: blueTip, alt: "Blue Tip", label: "5th Kup", path: "/5k" },
    { src: blueBelt, alt: "Blue Belt", label: "4th Kup", path: "/4k" },
    { src: redTip, alt: "Red Tip", label: "3rd Kup", path: "/3k" },
    { src: redBelt, alt: "Red Belt", label: "2nd Kup", path: "/2k" },
    { src: blackTip, alt: "Black Tip", label: "1st Kup", path: "/1k" },
    { src: danOne, alt: "1st Dan", label: "1st Dan", path: "/1d" },
    { src: danTwo, alt: "2nd Dan", label: "2nd Dan", path: "/2d" },
    { src: danThree, alt: "3rd Dan", label: "3rd Dan", path: "/3d" },
    { src: danFour, alt: "4th Dan", label: "4th Dan", path: "/4d" },
    { src: danFive, alt: "5th Dan", label: "5th Dan", path: "/5d" },
    { src: danSix, alt: "6th Dan", label: "6th Dan", path: "/6d" },
  ];

  const sparringItems = [
    { label: "3 Step Sparring", path: "/three-step", icon: threeStepIcon },
    { label: "2 Step Sparring", path: "/two-step", icon: twoStepIcon },
    { label: "Free Sparring", path: "/free-sparring", icon: freeSparringIcon },
  ];

  return (
    <>
      <Helmet>
        <title>TKD Study</title>
      </Helmet>

      <header className="main-header">
        <img src={logoImage} alt="TKD Logo" className="logo" />
        <h1 className="app-title">TKD Study</h1>
      </header>

      <div className="main-page">
        <section className="section">
          <Link to="/basic-theory">
            <img src={punchImage} alt="Basic Theory" className="section-image" />
            <h2>Basic Theory</h2>
          </Link>
        </section>

        <section className="section">
          <Link to="/technique">
            <img src={techImage} alt="Technique" className="section-image" />
            <h2>Technique</h2>
          </Link>
        </section>

        <section
          className="section"
          onClick={toggleBelts}
          style={{ cursor: "pointer", display: "flex", alignItems: "center", gap: "12px" }}
        >
          <img src={beltsImage} alt="Belts" className="section-image" />
          <h2 style={{ display: "flex", alignItems: "center", gap: "6px" }}>
            Belts {showBelts ? "▲" : "▼"}
          </h2>
        </section>

        {showBelts && (
          <div className="belt-gallery-container">
            {belts.map(({ src, alt, label, path }) => (
              <Link to={path} key={alt} className="belt-row" style={{ textDecoration: "none" }}>
                <img src={src} alt={alt} className="belt-image" />
                <span className="belt-label">{label}</span>
              </Link>
            ))}
          </div>
        )}

        <section className="section">
          <Link to="/korean">
            <img src={hanImage} alt="Korean" className="section-image" />
            <h2>Korean</h2>
          </Link>
        </section>

        <section className="section">
          <Link to="/BodyParts">
            <img src={bodyparts} alt="Body Parts" className="section-image" />
            <h2>Body Parts</h2>
          </Link>
        </section>

        <section
          className="section"
          onClick={toggleSparring}
          style={{ cursor: "pointer", display: "flex", alignItems: "center", gap: "12px" }}
        >
          <img src={stepImage} alt="Sparring" className="section-image" />
          <h2 style={{ display: "flex", alignItems: "center", gap: "6px" }}>
            Sparring {showSparring ? "▲" : "▼"}
          </h2>
        </section>

        {showSparring && (
          <div className="belt-gallery-container">
            {sparringItems.map(({ label, path, icon }) => (
              <Link to={path} key={label} className="belt-row" style={{ textDecoration: "none" }}>
                <img src={icon} alt={label} className="sparring-icon" />
                <span className="belt-label">{label}</span>
              </Link>
            ))}
          </div>
        )}

        <section className="section">
          <Link to="/stances">
            <img src={stancesImage} alt="Stances" className="section-image" />
            <h2>Stances</h2>
          </Link>
        </section>

        <section className="section">
          <Link to="/quiz">
            <img src={quizImage} alt="Theory Questions" className="section-image" />
            <h2>Theory Questions</h2>
          </Link>
        </section>

        <section className="section">
          <Link to="/sources">
            <img src={lemonImage} alt="Sources" className="section-image" />
            <h2>Sources</h2>
          </Link>
        </section>

        <section className="section">
          <Link to="/about">
            <img src={aboutImage} alt="App Info" className="section-image" />
            <h2>App Info</h2>
          </Link>
        </section>
      </div>
      <div className="social-icons">
        <img
          src={googlePlayImage}
          alt="Google Play Badge"
          className="store-badge"
          onClick={() =>
            window.open(
              "https://play.google.com/store/apps/details?id=com.tkd.app&pcampaignid=web_share",
              "_blank"
            )
          }
        />

        <img
          src={appStoreBadge}
          alt="Download on the App Store"
          className="store-badge"
          onClick={() =>
            window.open(
              "https://apps.apple.com/us/app/tkd-study-itf-taekwondo/id6774597426",
              "_blank"
            )
          }
        />
      </div>
    </>
  );
}


// Section page with back button
function SectionPage({ title, content }) {
  const navigate = useNavigate();

  return (
    <div className="section-page">
      <button className="back-button" onClick={() => navigate("/")}>
        &#8592; Back
      </button>
      <h1>{title}</h1>
      <p>{content}</p>
    </div>
  );
}


// App component with routing
function App() {
  return (
    <Router>
      <div className="App">
        <Helmet>
          <script
            async
            src="https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=ca-pub-1497178529208295"
            crossOrigin="anonymous"
          ></script>
        </Helmet>

        <ScrollToTop />

        <main className="container">
          <Routes>
            <Route path="/" element={<MainPage />} />

            {/* Auth */}
            <Route path="/login" element={<Login />} />

            <Route
              path="/handbook-editor"
              element={
                <ProtectedRoute>
                  <HandbookEditor />
                </ProtectedRoute>
              }
            />

            {/* Core Pages */}
            <Route path="/basic-theory" element={<BasicTheory />} />
            <Route path="/technique" element={<Technique />} />
            <Route path="/korean" element={<Korean />} />
            <Route path="/bodyparts" element={<BodyParts />} />
            <Route path="/korean-match" element={<KoreanMatch />} />
            <Route path="/stances" element={<Stances />} />
            <Route path="/quiz" element={<Quiz />} />
            <Route path="/sources" element={<Sources />} />
            <Route path="/about" element={<AppInfo />} />

            {/* Belt routes */}
            <Route path="/10k" element={<Belt10K />} />
            <Route path="/9k" element={<Belt9K />} />
            <Route path="/8k" element={<Belt8K />} />
            <Route path="/7k" element={<Belt7K />} />
            <Route path="/6k" element={<Belt6K />} />
            <Route path="/5k" element={<Belt5K />} />
            <Route path="/4k" element={<Belt4K />} />
            <Route path="/3k" element={<Belt3K />} />
            <Route path="/2k" element={<Belt2K />} />
            <Route path="/1k" element={<Belt1K />} />
            <Route path="/1d" element={<Belt1D />} />
            <Route path="/2d" element={<Belt2D />} />
            <Route path="/3d" element={<Belt3D />} />
            <Route path="/4d" element={<Belt4D />} />
            <Route path="/5d" element={<Belt5D />} />
            <Route path="/6d" element={<Belt6D />} />

            {/* Sparring */}
            <Route path="/three-step" element={<ThreeStep />} />
            <Route path="/two-step" element={<TwoStep />} />
            <Route path="/free-sparring" element={<FreeSparring />} />
          </Routes>
        </main>
      </div>
    </Router>
  );
}


export default App;
